import { gql, useQuery } from '@apollo/client';
import Modal from '@orn/shared-web/components/Modal/Modal';
import { cx } from '@orn/shared-web/utils/cx';
import * as React from 'react';
import { Link } from 'react-router-dom';

import AdminSelectUser from '../../components/admin/AdminSelectUser/AdminSelectUser';
import Button from '../../components/Button';
import InspectLink from '../../components/InspectLink';
import InspectLinkedText from '../../components/InspectLinkedText';
import Table from '../../components/Table';
import { assistantThreadDeepLinkPath, workItemsForSchedulePath } from '../../paths';

import { workItemScheduleFilterSignature } from './workItemSchedulesView';
import { abbreviateId, CELL_PREVIEW_MAX_CHARS, formatWorkItemTimestampFull, truncateForCell } from './workItemsView';
import { CELL_TRIGGER_CLASS, DateCell, EmailCell, ExpandableHeaderCell, IdCell } from './WorkItemTableCells';

import type {
  AdminListWorkItemSchedulesQuery,
  AdminListWorkItemSchedulesQueryVariables,
} from './__generated__/WorkItemSchedulesScene.gql';
import type { IAdminSelectUserSelection } from '../../components/admin/AdminSelectUser/AdminSelectUser';

const ADMIN_LIST_WORK_ITEM_SCHEDULES = gql`
  query AdminListWorkItemSchedules($beforeId: ID, $familyId: ID, $archived: Boolean) {
    adminListWorkItemSchedules(beforeId: $beforeId, familyId: $familyId, archived: $archived) {
      hasMore
      nextBeforeId
      schedules {
        id
        familyId
        familyMemberCount
        sourceThreadId
        createdByEmail
        cron
        timezone
        nextRunAt
        lastRunAt
        instruction
        defaultKey
        archivedAt
        emittedCount
        createdAt
        updatedAt
      }
    }
  }
`;

type ScheduleRow = AdminListWorkItemSchedulesQuery['adminListWorkItemSchedules']['schedules'][number];

/**
 * Column widths, in px, summing to the 1232px `main` gets at the 1280px floor.
 * Authoritative because the table is `table-fixed` — see the Items tab's own
 * widths for why that layout is the one to keep.
 */
const COLUMN_WIDTHS_PX = [92, 98, 96, 112, 98, 98, 200, 118, 148, 80, 92];

interface IInstructionCellProps {
  row: ScheduleRow;
  onOpen: (row: ScheduleRow) => void;
}

/** What the emitted item is told to do, previewed; clicking opens the full text. */
const InstructionCell = ({ row, onOpen }: IInstructionCellProps) => (
  <Table.Cell className="text-xs">
    <button type="button" className={CELL_TRIGGER_CLASS} onClick={() => onOpen(row)} title="Open the full instruction">
      <span className="text-fg-secondary line-clamp-3 break-words">
        {truncateForCell(row.instruction, CELL_PREVIEW_MAX_CHARS)}
      </span>
    </button>
  </Table.Cell>
);

interface IEmittedCellProps {
  row: ScheduleRow;
}

/**
 * How many items the schedule has emitted, linking into the Items tab narrowed
 * to them. A schedule that has never fired shows a plain zero — there is
 * nothing on the other side of the link to look at.
 */
const EmittedCell = ({ row }: IEmittedCellProps) => {
  if (row.emittedCount === 0) {
    return <Table.Cell className="text-fg-quaternary tabular-nums">0</Table.Cell>;
  }
  return (
    <Table.Cell className="text-xs tabular-nums">
      <Link
        to={workItemsForSchedulePath(row.id)}
        className="text-fg-brand-primary hover:text-fg-brand-secondary underline-offset-2 hover:underline"
        title="Show the work items this schedule emitted"
      >
        {row.emittedCount}
      </Link>
    </Table.Cell>
  );
};

interface IDefaultKeyCellProps {
  defaultKey: string | null;
}

/** Which seeded default this is; a dash means the assistant set the schedule up on its own. */
const DefaultKeyCell = ({ defaultKey }: IDefaultKeyCellProps) => {
  if (defaultKey === null) {
    return <Table.Cell className="text-fg-quaternary">—</Table.Cell>;
  }
  return (
    <Table.Cell mono className="truncate text-xs" title={defaultKey}>
      {defaultKey}
    </Table.Cell>
  );
};

interface IThreadCellProps {
  row: ScheduleRow;
}

/**
 * The thread the schedule was set up in — a deep link into the Threads
 * inspector, opened in a new tab so the operator's filter and paging state
 * survives the jump. The stamp is a hint, not a join key: a deleted thread
 * leaves it dangling, and the inspector's own empty state handles that landing.
 */
const ThreadCell = ({ row }: IThreadCellProps) => {
  const threadId = row.sourceThreadId ?? null;
  if (threadId === null) {
    return <Table.Cell className="text-fg-quaternary">—</Table.Cell>;
  }
  return (
    <Table.Cell mono className="whitespace-nowrap text-xs">
      <Link
        to={assistantThreadDeepLinkPath({ familyId: row.familyId, threadId })}
        target="_blank"
        rel="noopener noreferrer"
        className="text-fg-brand-primary hover:text-fg-brand-secondary underline-offset-2 hover:underline"
        title={threadId}
      >
        {abbreviateId(threadId)}
      </Link>
    </Table.Cell>
  );
};

interface IInstructionModalProps {
  row: ScheduleRow | null;
  onClose: () => void;
}

/** The schedule's instruction in full, monospaced and unabridged. */
const InstructionModal = ({ row, onClose }: IInstructionModalProps) => (
  <Modal isOpen={row !== null} onOpenChange={(open) => !open && onClose()} size="lg">
    <Modal.Header onClose={onClose}>Instruction</Modal.Header>
    <Modal.Body>
      {row !== null && (
        <div className="text-fg-secondary max-h-[60vh] overflow-y-auto whitespace-pre-wrap break-words font-mono text-xs">
          <InspectLinkedText text={row.instruction} />
        </div>
      )}
    </Modal.Body>
  </Modal>
);

interface IWorkItemSchedulesPaneProps {
  rows: ScheduleRow[];
  loading: boolean;
  hasMore: boolean;
  pageIndex: number;
  selection: IAdminSelectUserSelection | null;
  familyMemberCount: number | null;
  hideArchived: boolean;
  onToggleHideArchived: () => void;
  onSelectionChange: (selection: IAdminSelectUserSelection | null) => void;
  onOpenInstruction: (row: ScheduleRow) => void;
  onPrevPage: () => void;
  onNextPage: () => void;
}

/**
 * Presentational half of the Schedules tab — user filter above a dense table.
 * Split out from the container so the query/paging wiring stays separate from
 * the rendering.
 */
const BaseWorkItemSchedulesPane = ({
  rows,
  loading,
  hasMore,
  pageIndex,
  selection,
  familyMemberCount,
  hideArchived,
  onToggleHideArchived,
  onSelectionChange,
  onOpenInstruction,
  onPrevPage,
  onNextPage,
}: IWorkItemSchedulesPaneProps) => (
  <>
    <div className="border-border-primary flex flex-wrap items-start gap-4 border-b pb-4">
      <div className="flex flex-col gap-1">
        <label className="text-fg-tertiary text-xs font-medium uppercase tracking-wider">User</label>
        <AdminSelectUser
          selectedUserId={selection?.userId ?? null}
          onSelectionChange={onSelectionChange}
          placeholder="All users"
          hideLabel
          className="w-80"
        />
      </div>
      <div className="flex flex-col gap-1">
        <span className="text-fg-tertiary text-xs font-medium uppercase tracking-wider">Stopped</span>
        <Button
          color={hideArchived ? 'primary' : 'secondary'}
          size="sm"
          onClick={onToggleHideArchived}
          title="On by default, so only live schedules are listed; turn it off to see stopped ones alongside them."
        >
          Hide stopped
        </Button>
      </div>
    </div>

    {selection !== null && (
      <span className="text-fg-tertiary text-xs">
        Schedules are family-scoped — showing the whole family
        {selection.email ? ` of ${selection.email}` : ''} (
        <InspectLink id={selection.familyId} short />
        {familyMemberCount !== null ? `, ${familyMemberCount} member${familyMemberCount === 1 ? '' : 's'}` : ''}).
      </span>
    )}

    {loading && rows.length === 0 && <span className="text-fg-secondary text-xs">Loading…</span>}

    {!loading && rows.length === 0 && (
      <div className="text-fg-tertiary flex h-32 items-center justify-center text-xs italic">
        No schedules here. Nobody has asked the assistant for standing work, or the filter is too narrow.
      </div>
    )}

    {rows.length > 0 && (
      <>
        <Table className="table-fixed">
          <colgroup>
            {COLUMN_WIDTHS_PX.map((width, index) => (
              <col key={index} style={{ width }} />
            ))}
          </colgroup>
          <Table.Header sticky>
            <Table.Row>
              <Table.HeaderCell>Id</Table.HeaderCell>
              <Table.HeaderCell>Created</Table.HeaderCell>
              <Table.HeaderCell>Cron</Table.HeaderCell>
              <Table.HeaderCell>Timezone</Table.HeaderCell>
              <Table.HeaderCell>Next run</Table.HeaderCell>
              <Table.HeaderCell>Last run</Table.HeaderCell>
              <ExpandableHeaderCell>Instruction</ExpandableHeaderCell>
              <Table.HeaderCell>Default</Table.HeaderCell>
              <Table.HeaderCell>Created by</Table.HeaderCell>
              <Table.HeaderCell>Emitted</Table.HeaderCell>
              <Table.HeaderCell>Thread</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {rows.map((row) => {
              const archivedAt = row.archivedAt ?? null;
              return (
                // Stopping a schedule is one state, and dimming the row spends
                // no width to say it. `align-top` because cells default to
                // align-middle, which detaches the short cells from their row
                // once the instruction wraps.
                <Table.Row
                  key={row.id}
                  className={cx('[&>td]:align-top', archivedAt !== null && 'opacity-45')}
                  title={archivedAt !== null ? `Stopped ${formatWorkItemTimestampFull(archivedAt)}` : undefined}
                >
                  <IdCell id={row.id} />
                  <DateCell iso={row.createdAt} />
                  <Table.Cell mono className="whitespace-nowrap text-xs" title={row.cron}>
                    {row.cron}
                  </Table.Cell>
                  <Table.Cell mono className="truncate text-xs" title={row.timezone}>
                    {row.timezone}
                  </Table.Cell>
                  {/* A stopped schedule keeps whatever `nextRunAt` it held, so
                      the value is a leftover rather than an upcoming run. */}
                  <DateCell iso={row.nextRunAt} />
                  <DateCell iso={row.lastRunAt ?? null} />
                  <InstructionCell row={row} onOpen={onOpenInstruction} />
                  <DefaultKeyCell defaultKey={row.defaultKey ?? null} />
                  <EmailCell email={row.createdByEmail ?? null} />
                  <EmittedCell row={row} />
                  <ThreadCell row={row} />
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table>

        <div className="flex items-center gap-3">
          <Button color="secondary" size="xs" onClick={onPrevPage} isDisabled={pageIndex === 0 || loading}>
            Prev
          </Button>
          <Button color="secondary" size="xs" onClick={onNextPage} isDisabled={!hasMore || loading}>
            Next
          </Button>
          <span className="text-fg-tertiary text-xs">
            Page {pageIndex + 1} · {rows.length} row{rows.length === 1 ? '' : 's'}
          </span>
          {!hideArchived && <span className="text-fg-tertiary text-xs">Dimmed rows are stopped</span>}
        </div>
      </>
    )}
  </>
);

/**
 * Schedules tab of the Work items tool. Lists the standing instructions the
 * assistant holds across every family, newest-first by creation, with the cron
 * that decides when each comes due, the zone it is read in, and both run
 * stamps — which is what a misfire is diagnosed from. The Emitted count links
 * into the Items tab narrowed to the work that schedule has raised.
 *
 * The User filter sends `familyId`, not `userId`: schedules are family-scoped.
 * Read-only: setting up, editing, and stopping a schedule happen through the
 * assistant in the consumer product.
 */
const WorkItemSchedulesScene = () => {
  const [selection, setSelection] = React.useState<IAdminSelectUserSelection | null>(null);
  const [hideArchived, setHideArchived] = React.useState(true);
  // Forward-only keyset, so Prev pops a stack rather than querying backwards.
  const [cursorStack, setCursorStack] = React.useState<string[]>([]);
  const [instructionRow, setInstructionRow] = React.useState<ScheduleRow | null>(null);

  const { data, previousData, loading } = useQuery<
    AdminListWorkItemSchedulesQuery,
    AdminListWorkItemSchedulesQueryVariables
  >(ADMIN_LIST_WORK_ITEM_SCHEDULES, {
    variables: {
      beforeId: cursorStack.at(-1) ?? null,
      familyId: selection?.familyId ?? null,
      // Null is "both"; the tab's own default is to leave stopped schedules out.
      archived: hideArchived ? false : null,
    },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  // Hold the last page on screen across a cursor move but not across a filter
  // change — see the Items tab, which carries the same reasoning in full.
  const filterSignature = workItemScheduleFilterSignature({
    familyId: selection?.familyId ?? null,
    hideArchived,
  });
  const renderedSignature = React.useRef(filterSignature);
  const onlyCursorMoved = renderedSignature.current === filterSignature;
  React.useEffect(() => {
    renderedSignature.current = filterSignature;
  }, [filterSignature]);

  const reusablePage = onlyCursorMoved ? previousData : undefined;
  const listing = (data ?? reusablePage)?.adminListWorkItemSchedules;
  const rows = listing?.schedules ?? [];

  // A stale cursor starts mid-window under the new filter, so every filter
  // change restarts paging.
  const handleSelectionChange = (next: IAdminSelectUserSelection | null) => {
    setSelection(next);
    setCursorStack([]);
  };

  const handleToggleHideArchived = () => {
    setHideArchived((prev) => !prev);
    setCursorStack([]);
  };

  const handleNextPage = () => {
    const nextBeforeId = listing?.nextBeforeId;
    if (listing?.hasMore && nextBeforeId) {
      setCursorStack((prev) => [...prev, nextBeforeId]);
    }
  };

  const handlePrevPage = () => setCursorStack((prev) => prev.slice(0, -1));

  return (
    <>
      <BaseWorkItemSchedulesPane
        rows={rows}
        loading={loading}
        hasMore={listing?.hasMore ?? false}
        pageIndex={cursorStack.length}
        selection={selection}
        familyMemberCount={rows[0]?.familyMemberCount ?? null}
        hideArchived={hideArchived}
        onToggleHideArchived={handleToggleHideArchived}
        onSelectionChange={handleSelectionChange}
        onOpenInstruction={setInstructionRow}
        onPrevPage={handlePrevPage}
        onNextPage={handleNextPage}
      />
      <InstructionModal row={instructionRow} onClose={() => setInstructionRow(null)} />
    </>
  );
};

export default WorkItemSchedulesScene;
